import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, FileText, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

const Blog = () => {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog`).then((res) => setPosts(res.data)).catch(() => setPosts([]));
  }, []);

  return (
    <>
      <SEO title="IT Blog & Guides for Adelaide SMEs | UMI IT Services" description="Plain-English guides on IT support, cybersecurity and cloud for South Australian small businesses, from the UMI IT Services team." />

      <section className="bg-[#F8FAFC] pt-28 pb-16 md:pt-36 md:pb-20">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">Blog &amp; guides</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-[#0B2545]">
            IT advice in <span className="text-[#17B8A6]">plain English.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Practical guides on IT support, cybersecurity and cloud for South Australian small businesses — written by the people who actually do the work.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {posts === null ? (
            <div className="flex justify-center py-12" data-testid="blog-loading">
              <Loader2 size={32} className="animate-spin text-[#17B8A6]" aria-label="Loading articles" />
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-slate-500 py-12" data-testid="blog-empty">Articles are on their way — check back soon.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="blog-list">
              {posts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                  className="group rounded-2xl border border-slate-200 p-6 sm:p-8 hover:border-[#17B8A6] hover:shadow-lg transition-all duration-200 hover:-translate-y-1 flex flex-col">
                  {p.image && (
                    <img src={p.image} alt={p.title} loading="lazy"
                      className="-mx-6 -mt-6 sm:-mx-8 sm:-mt-8 mb-5 w-[calc(100%+3rem)] sm:w-[calc(100%+4rem)] max-w-none h-44 object-cover rounded-t-2xl" />
                  )}
                  <p className="text-xs font-bold uppercase tracking-wider text-[#0D9488]">{p.category}</p>
                  <h2 className="mt-3 text-lg sm:text-xl font-semibold text-[#0B2545] leading-snug">{p.title}</h2>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                    <span>{formatDate(p.published_at)}</span>
                    <span className="inline-flex items-center gap-1.5"><FileText size={13} aria-hidden="true" /> {p.reading_time} min read</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#0B2545] py-16 md:py-20">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Prefer to just talk it through?</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">Skip the reading — book a free consultation and get answers specific to your business.</p>
          <Link to="/contact" data-testid="blog-cta-consultation"
            className="mt-8 inline-flex items-center gap-2 bg-[#17B8A6] hover:bg-[#0D9488] text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 hover:-translate-y-0.5">
            Book a Free Consultation <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Blog;
